<template>
  <div class="call-popup" :class="[
    `call-popup--${call.direction}`,
    `call-popup--${call.status}`,
    { 'call-popup--minimized': isMinimized },
    { 'call-popup--dark': isDarkTheme }
  ]">
    <!-- Header with gradient based on call state -->
    <div class="call-popup__header" @click="toggleMinimize">
      <!-- Auto-dialer progress badge -->
      <div v-if="isAutoDialing" class="call-popup__auto-badge">
        <v-icon x-small color="white" class="mr-1">mdi-play-circle</v-icon>
        {{ autoDialerProgress.current }}/{{ autoDialerProgress.total }}
      </div>

      <div class="call-popup__header-content">
        <!-- Avatar -->
        <div class="call-popup__avatar" :class="{ 'avatar--pulsing': isPulsing }">
          <v-icon size="24" color="white">
            {{ getAvatarIcon }}
          </v-icon>
          <div v-if="isPulsing" class="avatar-pulse-ring"></div>
          <div v-if="isPulsing" class="avatar-pulse-ring delay-1"></div>
        </div>

        <!-- Call Info -->
        <div class="call-popup__info">
          <div class="call-popup__name" :title="displayName">
            {{ displayName }}
          </div>
          <div class="call-popup__number">
            {{ formatPhoneNumber(call.number) }}
          </div>
        </div>

        <!-- Status/Duration -->
        <div class="call-popup__status">
          <div v-if="call.status === 'connected'" class="call-popup__duration">
            {{ formatDuration(call.duration) }}
          </div>
          <div v-else class="call-popup__status-text">
            <span class="status-dot" :class="`dot--${call.status}`"></span>
            {{ statusText }}
          </div>
        </div>

        <!-- Minimize button -->
        <v-btn icon x-small class="call-popup__minimize" @click.stop="toggleMinimize">
          <v-icon small color="white">
            {{ isMinimized ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
          </v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Body - Hidden when minimized -->
    <v-expand-transition>
      <div v-show="!isMinimized" class="call-popup__body">
        <!-- Additional Info for Incoming Calls -->
        <div v-if="call.direction === 'incoming'" class="call-popup__incoming-info">
          <div v-if="call.did" class="call-popup__did">
            <v-icon x-small color="grey">mdi-phone-forward</v-icon>
            <span>DID: {{ call.did }}</span>
          </div>

          <!-- Call Strategy Badge (for incoming) -->
          <div v-if="call.strategy" class="call-popup__strategy">
            <v-chip x-small :color="getStrategyColor" text-color="white" class="strategy-chip">
              <v-icon x-small left>{{ getStrategyIcon }}</v-icon>
              {{ getStrategyLabel }}
            </v-chip>
          </div>
        </div>

        <!-- Can't Hear button (for mobile audio issues) - shown only when connected -->
        <div v-if="showCantHear && call.status === 'connected'" class="cant-hear-container">
          <v-btn 
            x-small 
            text
            color="orange" 
            class="cant-hear-btn"
            title="Tap if you can't hear the other person"
            @click="forceAudio">
            <v-icon x-small left>mdi-volume-off</v-icon>
            Can't hear?
          </v-btn>
        </div>

        <!-- Action Buttons -->
        <div class="call-popup__actions">
          <!-- Incoming Call - Not Connected (ringing, connecting, or any non-connected state) -->
          <template v-if="call.direction === 'incoming' && !['connected', 'ended', 'failed'].includes(call.status)">
            <v-btn fab color="red darken-1" dark class="action-btn action-btn--reject" :disabled="isProcessing"
              @click="rejectCall">
              <v-icon>mdi-phone-hangup</v-icon>
            </v-btn>
            <v-btn fab color="green darken-1" dark class="action-btn action-btn--accept" :disabled="isProcessing"
              @click="acceptCall">
              <v-icon>mdi-phone</v-icon>
            </v-btn>
          </template>

          <!-- Connected Call (Both Incoming & Outgoing) -->
          <template v-else-if="call.status === 'connected'">
            <!-- Mute -->
            <v-btn fab x-small :color="isMuted ? 'red' : 'grey lighten-2'" :dark="isMuted" class="action-btn"
              @click="toggleMute">
              <v-icon small>{{ isMuted ? 'mdi-microphone-off' : 'mdi-microphone' }}</v-icon>
            </v-btn>

            <!-- Hold -->
            <v-btn fab x-small :color="isOnHold ? 'orange' : 'grey lighten-2'" :dark="isOnHold" class="action-btn"
              @click="toggleHold">
              <v-icon small>{{ isOnHold ? 'mdi-play' : 'mdi-pause' }}</v-icon>
            </v-btn>

            <!-- End Call -->
            <v-btn fab small color="red" dark class="action-btn action-btn--end" :disabled="isProcessing"
              @click="endCall">
              <v-icon>mdi-phone-hangup</v-icon>
            </v-btn>

            <!-- Dialpad Toggle -->
            <v-btn fab x-small :color="showDialpad ? 'primary' : 'grey lighten-2'" :dark="showDialpad"
              class="action-btn" @click="toggleDialpad">
              <v-icon small>mdi-dialpad</v-icon>
            </v-btn>
          </template>

          <!-- Outgoing - Not yet connected -->
          <template v-else-if="call.direction === 'outgoing'">
            <v-btn fab small color="red" dark class="action-btn action-btn--end" :disabled="isProcessing"
              @click="endCall">
              <v-icon>mdi-phone-hangup</v-icon>
            </v-btn>
          </template>

          <!-- Fallback for ended/failed calls -->
          <template v-else>
            <span class="grey--text text-caption">Call {{ call.status }}</span>
          </template>
        </div>

        <!-- DTMF Dialpad -->
        <v-expand-transition>
          <div v-show="showDialpad && call.status === 'connected'" class="call-popup__dialpad">
            <div class="dialpad-grid">
              <button v-for="digit in dialpadDigits" :key="digit.num" class="dialpad-btn" @click="sendDTMF(digit.num)">
                <span class="digit-main">{{ digit.num }}</span>
                <span class="digit-sub">{{ digit.letters }}</span>
              </button>
            </div>
          </div>
        </v-expand-transition>
      </div>
    </v-expand-transition>

    <!-- Hidden Audio Element with mobile-specific attributes -->
    <audio 
      ref="callAudio" 
      autoplay 
      playsinline 
      webkit-playsinline
      x-webkit-airplay="allow"
      style="display: none;"
    ></audio>
  </div>
</template>

<script>
export default {
  name: 'CallPopup',

  props: {
    call: {
      type: Object,
      required: true,
      // Expected shape:
      // {
      //   id: string,
      //   direction: 'incoming' | 'outgoing',
      //   status: 'ringing' | 'calling' | 'connecting' | 'connected' | 'ended' | 'failed',
      //   number: string,
      //   contactName: string,
      //   duration: number,
      //   did: string (for incoming),
      //   strategy: string (for incoming),
      //   callerName: string (for incoming)
      // }
    },
    index: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      isMinimized: false,
      isProcessing: false,
      isMuted: false,
      isOnHold: false,
      showDialpad: false,
      showCantHear: true, // Show "Can't hear?" button on mobile
      dialpadDigits: [
        { num: '1', letters: '' },
        { num: '2', letters: 'ABC' },
        { num: '3', letters: 'DEF' },
        { num: '4', letters: 'GHI' },
        { num: '5', letters: 'JKL' },
        { num: '6', letters: 'MNO' },
        { num: '7', letters: 'PQRS' },
        { num: '8', letters: 'TUV' },
        { num: '9', letters: 'WXYZ' },
        { num: '*', letters: '' },
        { num: '0', letters: '+' },
        { num: '#', letters: '' },
      ]
    };
  },

  computed: {
    isDarkTheme() {
      return this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.dark;
    },

    isAutoDialing() {
      return this.$store.state.dialer.isAutoDialing;
    },

    autoDialerProgress() {
      return this.$store.getters['dialer/autoDialerProgress'];
    },

    displayName() {
      return this.call.contactName || this.call.callerName || this.call.displayName || 'Unknown';
    },

    statusText() {
      const statusMap = {
        ringing: this.call.direction === 'incoming' ? 'Incoming...' : 'Ringing...',
        calling: 'Calling...',
        connecting: 'Connecting...',
        connected: 'Connected',
        ended: 'Call Ended',
        failed: 'Failed'
      };
      return statusMap[this.call.status] || this.call.status;
    },

    isPulsing() {
      return ['ringing', 'calling', 'connecting'].includes(this.call.status);
    },

    getAvatarIcon() {
      if (this.call.status === 'connected') return 'mdi-phone-in-talk';
      if (this.call.direction === 'incoming') return 'mdi-phone-incoming';
      return 'mdi-phone-outgoing';
    },

    getStrategyColor() {
      const colors = {
        agent: 'blue',
        'random-agent': 'orange',
        unassigned: 'grey'
      };
      return colors[this.call.strategy] || 'grey';
    },

    getStrategyIcon() {
      const icons = {
        agent: 'mdi-account',
        'random-agent': 'mdi-shuffle-variant',
        unassigned: 'mdi-cancel'
      };
      return icons[this.call.strategy] || 'mdi-help-circle';
    },

    getStrategyLabel() {
      const labels = {
        agent: 'Direct',
        'random-agent': 'Random',
        unassigned: 'Unassigned'
      };
      return labels[this.call.strategy] || this.call.strategy;
    }
  },

  watch: {
    'call.status'(newStatus, oldStatus) {
      console.log('[CallPopup] 📊 Status changed:', oldStatus, '->', newStatus, 'direction:', this.call.direction);
      // Reset states when call ends
      if (newStatus === 'ended' || newStatus === 'failed') {
        this.isMuted = false;
        this.isOnHold = false;
        this.showDialpad = false;
      }
    },
    call: {
      handler(newCall) {
        console.log('[CallPopup] 📊 Call prop updated:', {
          direction: newCall.direction,
          status: newCall.status,
          number: newCall.number
        });
      },
      deep: true,
      immediate: true
    }
  },

  methods: {
    async forceAudio() {
      console.log('[CallPopup] 🔊 User clicked "Can\'t hear?" - forcing audio retry');
      try {
        await this.$store.dispatch('dialer/forceRetryAudio');
        // Also try to force play the local audio element
        const audioEl = this.$refs.callAudio;
        if (audioEl) {
          audioEl.muted = false;
          audioEl.volume = 1.0;
          try {
            await audioEl.play();
            console.log('[CallPopup] ✅ Local audio element play() succeeded');
          } catch (err) {
            console.warn('[CallPopup] ⚠️ Local audio play error:', err);
          }
        }
        // Hide button after successful click (user interaction unlocks audio)
        this.showCantHear = false;
        setTimeout(() => {
          this.showCantHear = true; // Show again in case it still doesn't work
        }, 5000);
      } catch (error) {
        console.error('[CallPopup] ❌ Force audio failed:', error);
      }
    },

    formatPhoneNumber(phone) {
      if (!phone) return 'Unknown';
      const cleaned = String(phone).replace(/\D/g, '');
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      }
      if (cleaned.length === 11 && cleaned[0] === '1') {
        return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
      }
      return phone;
    },

    formatDuration(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },

    toggleMinimize() {
      this.isMinimized = !this.isMinimized;
    },

    async acceptCall() {
      if (this.isProcessing) {
        console.log('[CallPopup] Accept already processing, ignoring click');
        return;
      }
      this.isProcessing = true;
      console.log('[CallPopup] 📞 Accepting incoming call...');
      try {
        await this.$store.dispatch('dialer/answerIncomingCall', {
          audioElement: this.$refs.callAudio
        });
        console.log('[CallPopup] ✅ Call accepted successfully');
        // Keep isProcessing true briefly to prevent accidental end-call clicks
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error('[CallPopup] ❌ Error accepting call:', error);
      } finally {
        this.isProcessing = false;
      }
    },

    async rejectCall() {
      if (this.isProcessing) {
        console.log('[CallPopup] Reject already processing, ignoring click');
        return;
      }
      this.isProcessing = true;
      try {
        await this.$store.dispatch('dialer/rejectIncomingCall');
      } catch (error) {
        console.error('[CallPopup] Error rejecting call:', error);
      } finally {
        this.isProcessing = false;
      }
    },

    async endCall() {
      if (this.isProcessing) {
        console.log('[CallPopup] End call already processing, ignoring click');
        return;
      }
      this.isProcessing = true;
      console.log('[CallPopup] 📴 Ending call...');
      try {
        await this.$store.dispatch('dialer/endCall');
      } catch (error) {
        console.error('[CallPopup] Error ending call:', error);
      } finally {
        this.isProcessing = false;
      }
    },

    toggleMute() {
      console.log('[CallPopup] toggleMute called, current isMuted:', this.isMuted);
      this.isMuted = !this.isMuted;
      console.log('[CallPopup] Dispatching dialer/toggleMute with:', this.isMuted);
      this.$store.dispatch('dialer/toggleMute', this.isMuted);
    },

    toggleHold() {
      console.log('[CallPopup] toggleHold called, current isOnHold:', this.isOnHold);
      this.isOnHold = !this.isOnHold;
      console.log('[CallPopup] Dispatching dialer/toggleHold with:', this.isOnHold);
      this.$store.dispatch('dialer/toggleHold', this.isOnHold);
    },

    toggleDialpad() {
      this.showDialpad = !this.showDialpad;
    },

    sendDTMF(digit) {
      this.$store.dispatch('dialer/sendDTMF', digit);
    }
  }
};
</script>

<style scoped>
.call-popup {
  width: 340px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  margin-bottom: 12px;
}

.call-popup--incoming {
  width: 360px;
}

.call-popup--minimized {
  width: 280px;
}

/* Header Styles */
.call-popup__header {
  padding: 16px 18px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

/* Auto-dialer badge */
.call-popup__auto-badge {
  position: absolute;
  top: 4px;
  right: 8px;
  background: rgba(0, 0, 0, 0.3);
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  display: flex;
  align-items: center;
}

/* Gradient based on direction and status */
.call-popup--incoming .call-popup__header {
  background: linear-gradient(135deg, #43a047 0%, #81c784 100%);
}

.call-popup--outgoing .call-popup__header {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
}

.call-popup--connected .call-popup__header {
  background: linear-gradient(135deg, #2e7d32 0%, #4caf50 100%);
}

.call-popup--incoming.call-popup--ringing .call-popup__header {
  background: linear-gradient(135deg, #ff9800 0%, #ffb74d 100%);
  animation: incomingPulse 1.5s ease-in-out infinite alternate;
}

.call-popup--ringing .call-popup__header,
.call-popup--calling .call-popup__header {
  animation: headerPulse 2s ease-in-out infinite alternate;
}

.call-popup--failed .call-popup__header {
  background: linear-gradient(135deg, #c62828 0%, #ef5350 100%);
}

.call-popup__header-content {
  display: flex;
  align-items: center;
  gap: 14px;
}

/* Avatar */
.call-popup__avatar {
  position: relative;
  width: 52px;
  height: 52px;
  min-width: 52px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.call-popup--incoming .call-popup__avatar {
  width: 56px;
  height: 56px;
  min-width: 56px;
}

.avatar--pulsing {
  animation: avatarScale 1.5s ease-in-out infinite;
}

.avatar-pulse-ring {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  animation: pulseRing 1.5s ease-out infinite;
}

.avatar-pulse-ring.delay-1 {
  animation-delay: 0.5s;
}

/* Info */
.call-popup__info {
  flex: 1;
  min-width: 0;
  color: white;
}

.call-popup__name {
  font-size: 17px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.call-popup--incoming .call-popup__name {
  font-size: 18px;
}

.call-popup__number {
  font-size: 13px;
  opacity: 0.9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
}

/* Status */
.call-popup__status {
  text-align: right;
  color: white;
}

.call-popup__duration {
  font-size: 20px;
  font-weight: 700;
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;
}

.call-popup__status-text {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  animation: blink 1s ease-in-out infinite;
}

.dot--ringing,
.dot--calling {
  background: #fff;
}

.dot--connecting {
  background: #ffeb3b;
}

.dot--connected {
  background: #69f0ae;
}

.dot--failed {
  background: #ff5252;
}

.call-popup__minimize {
  opacity: 0.8;
  transition: opacity 0.2s;
}

.call-popup__minimize:hover {
  opacity: 1;
}

/* Body */
.call-popup__body {
  padding: 16px 18px 20px;
  background: #ffffff;
}

/* Incoming Call Info */
.call-popup__incoming-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.call-popup__did {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #666;
  background: #f5f5f5;
  padding: 4px 10px;
  border-radius: 12px;
}

.call-popup__strategy {
  margin-left: auto;
}

.strategy-chip {
  font-weight: 600;
}

/* Actions */
.call-popup__actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  flex-wrap: nowrap;
}

.action-btn {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.action-btn:hover {
  transform: scale(1.08);
}

.action-btn--accept {
  width: 56px !important;
  height: 56px !important;
  animation: pulseGreen 1.5s ease-in-out infinite;
}

.action-btn--accept .v-icon {
  font-size: 26px !important;
}

.action-btn--reject {
  width: 56px !important;
  height: 56px !important;
}

.action-btn--reject .v-icon {
  font-size: 26px !important;
}

.action-btn--end {
  animation: pulseRed 2s ease-in-out infinite;
}

/* Can't hear button container */
.cant-hear-container {
  text-align: center;
  margin-bottom: 4px;
}

.cant-hear-btn {
  font-size: 11px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

/* Dialpad */
.call-popup__dialpad {
  margin-top: 12px;
  background: white;
  border-radius: 12px;
  padding: 8px;
}

.dialpad-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}

.dialpad-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 48px;
  border: none;
  border-radius: 8px;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.15s ease;
}

.dialpad-btn:hover {
  background: #e0e0e0;
}

.dialpad-btn:active {
  background: #1976d2;
  color: white;
}

.dialpad-btn:active .digit-sub {
  color: rgba(255, 255, 255, 0.7);
}

.digit-main {
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
}

.digit-sub {
  font-size: 8px;
  color: #9e9e9e;
  letter-spacing: 1px;
  margin-top: 2px;
}

/* Animations */
@keyframes headerPulse {
  0% {
    filter: brightness(1);
  }

  100% {
    filter: brightness(1.1);
  }
}

@keyframes incomingPulse {
  0% {
    filter: brightness(1);
    box-shadow: inset 0 0 20px rgba(255, 255, 255, 0);
  }

  100% {
    filter: brightness(1.15);
    box-shadow: inset 0 0 20px rgba(255, 255, 255, 0.2);
  }
}

@keyframes avatarScale {

  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.08);
  }
}

@keyframes pulseRing {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }

  100% {
    transform: scale(1.6);
    opacity: 0;
  }
}

@keyframes blink {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.4;
  }
}

@keyframes pulseGreen {

  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }

  50% {
    box-shadow: 0 0 0 12px rgba(76, 175, 80, 0);
  }
}

@keyframes pulseRed {

  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.5);
  }

  50% {
    box-shadow: 0 0 0 8px rgba(244, 67, 54, 0);
  }
}

/* Dialpad */
.call-popup__dialpad {
  margin-top: 12px;
  background: #f5f5f5;
  border-radius: 12px;
  padding: 8px;
}

.dialpad-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}

.dialpad-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 48px;
  border: none;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.15s ease;
}

.dialpad-btn:hover {
  background: #e0e0e0;
}

.dialpad-btn:active {
  background: #1976d2;
  color: white;
}

.dialpad-btn:active .digit-sub {
  color: rgba(255, 255, 255, 0.7);
}

.digit-main {
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
}

.digit-sub {
  font-size: 8px;
  color: #9e9e9e;
  letter-spacing: 1px;
  margin-top: 2px;
}

/* Dark Mode */
@media (prefers-color-scheme: dark) {
  .call-popup {
    background: #1e1e1e;
  }

  .call-popup__body {
    background: #252525;
  }

  .call-popup__incoming-info {
    border-bottom-color: #333;
  }

  .call-popup__did {
    background: #333;
    color: #aaa;
  }

  .call-popup__dialpad {
    background: #1e1e1e;
  }

  .dialpad-btn {
    background: #333;
    color: white;
  }

  .dialpad-btn:hover {
    background: #444;
  }
}

/* Vuetify Dark Theme Support */
.theme--dark .call-popup,
.call-popup--dark {
  background: #2d2d2d;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.theme--dark .call-popup__body,
.call-popup--dark .call-popup__body {
  background: #363636;
}

.theme--dark .call-popup__incoming-info,
.call-popup--dark .call-popup__incoming-info {
  border-bottom-color: #4a4a4a;
}

.theme--dark .call-popup__did,
.call-popup--dark .call-popup__did {
  background: #4a4a4a;
  color: #c0c0c0;
}

.theme--dark .call-popup__did .v-icon,
.call-popup--dark .call-popup__did .v-icon {
  color: #999 !important;
}

.theme--dark .call-popup__dialpad,
.call-popup--dark .call-popup__dialpad {
  background: #2d2d2d;
}

.theme--dark .dialpad-btn,
.call-popup--dark .dialpad-btn {
  background: #4a4a4a;
  color: #e0e0e0;
}

.theme--dark .dialpad-btn:hover,
.call-popup--dark .dialpad-btn:hover {
  background: #5a5a5a;
}

.theme--dark .dialpad-btn:active,
.call-popup--dark .dialpad-btn:active {
  background: #1976d2;
  color: white;
}

.theme--dark .digit-sub,
.call-popup--dark .digit-sub {
  color: #888;
}

/* Responsive */
@media (max-width: 400px) {
  .call-popup {
    width: 100% !important;
  }

  .call-popup__actions {
    gap: 20px;
  }

  .action-btn--accept {
    width: 56px !important;
    height: 56px !important;
  }
}
</style>
